import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getCurrentUser,
  fetchAuthSession,
  signInWithRedirect,
} from "aws-amplify/auth";

import { RootState } from "app/store";
import { rememberPostAuthPath } from "./postAuthPath";

interface AuthPayload {
  isAuthenticated: true;
  jwtToken: string;
  expiration: number;
  roles: string[];
  user: {
    alias: string;
    userId: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
  };
}

export const authenticate = createAsyncThunk(
  "auth/authenticate",
  async (expired: boolean, { getState }) => {
    let isSignedIn = false;

    const buildAuthPayload = (
      session: Awaited<ReturnType<typeof fetchAuthSession>>,
    ): AuthPayload => {
      const idToken = session.tokens?.idToken;
      const claims = (idToken?.payload ?? {}) as Record<string, unknown>;

      let userAlias = "";
      const identitiesRaw = claims.identities;
      if (typeof identitiesRaw === "string") {
        try {
          const parsed = JSON.parse(identitiesRaw);
          userAlias = Array.isArray(parsed)
            ? (parsed[0]?.userId ?? "")
            : (parsed?.userId ?? "");
        } catch {
          userAlias = "";
        }
      } else if (Array.isArray(identitiesRaw)) {
        userAlias = (identitiesRaw[0] as { userId?: string })?.userId ?? "";
      }

      const givenName = (claims.given_name as string) ?? "";
      const familyName = (claims.family_name as string) ?? "";

      return {
        isAuthenticated: true,
        jwtToken: idToken?.toString() ?? "",
        expiration: (claims.exp as number) ?? 0,
        roles: (claims["custom:ldapGroups"] as string[]) ?? [],
        user: {
          alias: userAlias,
          userId: (claims.sub as string) ?? "",
          firstName: givenName,
          lastName: familyName,
          fullName: `${givenName} ${familyName}`.trim(),
          email: (claims.email as string) ?? "",
        },
      };
    };

    try {
      await getCurrentUser();
      isSignedIn = true;
      const session = await fetchAuthSession(
        expired ? { forceRefresh: true } : undefined,
      );
      return buildAuthPayload(session);
    } catch (_e) {
      if (isSignedIn) throw _e;

      const { providerName } = (getState() as RootState).settings.auth;
      try {
        // Cognito returns to the origin, so stash the intended path first; the
        // `/` route restores it. Deliberately not `customState`: that arrives as
        // a Hub event whose navigate races the `/` -> `/home` redirect.
        rememberPostAuthPath();
        await signInWithRedirect({
          provider: { custom: providerName },
        });
        // Redirect navigates away — thunk resolves with undefined while navigation occurs
      } catch (redirectError: unknown) {
        if (
          redirectError instanceof Error &&
          redirectError.name === "UserAlreadyAuthenticatedException"
        ) {
          // Session exists — recover by re-running the auth flow
          await getCurrentUser();
          const session = await fetchAuthSession();
          return buildAuthPayload(session);
        }
        throw redirectError;
      }
    }
  },
);
